import { useState, useEffect } from "react"
import { FileText, Image as ImageIcon, Video, HelpCircle, Download, Plus, Edit, Trash2, Eye } from "lucide-react"
import { Button } from "../ui/button"
import TipTapEditor from "../TipTapEditor"
import ImageUpload from "../ImageUpload"
import VideoBlock from "../course/VideoBlock"
import TextBlock from "../course/TextBlock"
import ImageBlock from "../course/ImageBlock"
import AssignmentBlock from "../course/AssignmentBlock"
import DownloadBlock from "../course/DownloadBlock"
import Card, { CardContent } from "../Card"
import { Loader2 } from "lucide-react"
import ConfirmModal from "./ConfirmModal"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"

export default function EditorCanvas({
  selectedModuleId,
  selectedBlockId,
  selectedBlock,
  onBlockUpdate,
  onBlockDelete,
  blocksLoading,
  onCreateBlock,
  isCreatingNew = false
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState(null)
  const [previewMode, setPreviewMode] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [imageFile, setImageFile] = useState(null) // Stocker le fichier image sélectionné
  const [uploadingImage, setUploadingImage] = useState(false) // État de chargement lors de l'upload

  // Initialize edit data when block is selected
  useEffect(() => {
    if (selectedBlock) {
      setEditData({
        title: selectedBlock.title || '',
        content: selectedBlock.content || {},
        type: selectedBlock.type,
        duration: selectedBlock.duration || 0
      })
      setIsEditing(false)
      setPreviewMode(false)
      setImageFile(null) // Réinitialiser le fichier image
    }
  }, [selectedBlock])

  // Fonction pour uploader l'image et obtenir une URL
  const uploadImageToServer = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', 'course-blocks')
    
    try {
      const response = await api.post(endpoints.cloudinaryUpload, formData)
      
      if (response?.success && response?.data?.url) {
        return response.data.url
      } else if (response?.data?.url) {
        return response.data.url
      } else if (response?.url) {
        return response.url
      } else {
        throw new Error("Format de réponse inattendu de l'API d'upload")
      }
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image:", error)
      const errorMessage = error?.response?.data?.message 
        ? (Array.isArray(error.response.data.message) 
            ? error.response.data.message[0] 
            : error.response.data.message)
        : error?.message || "Erreur lors de l'upload de l'image"
      throw new Error(errorMessage)
    }
  }

  const handleSave = async () => {
    if (!selectedBlock || !editData) return

    try {
      setUploadingImage(selectedBlock.type === 'IMAGE' && imageFile ? true : false)
      
      // Préparer le content selon le type de bloc
      let contentToSave = { ...editData.content }
      
      // Pour les blocs IMAGE, préparer le content avec alt et caption
      if (selectedBlock.type === 'IMAGE') {
        // Si un nouveau fichier est sélectionné, on enverra le fichier directement
        // Le content doit contenir alt et caption (sans imageUrl car il sera généré par le backend)
        if (imageFile) {
          // Nouveau fichier : content ne contient que alt et caption
          // Le backend générera automatiquement imageUrl
          contentToSave = {
            alt: editData.content?.alt || editData.title || "Image",
            caption: editData.content?.caption || ""
          }
        } else {
          // Pas de nouveau fichier : conserver les propriétés existantes
          // Si imageUrl existe et n'est pas une data URL, la conserver
          if (editData.content?.imageUrl && !editData.content.imageUrl.startsWith('data:')) {
            contentToSave = {
              ...editData.content,
              alt: editData.content?.alt || editData.title || "Image",
              caption: editData.content?.caption || ""
            }
          } else if (editData.content?.imageUrl && editData.content.imageUrl.startsWith('data:')) {
            // Data URL : ne pas l'envoyer, utiliser seulement alt et caption
            throw new Error('Veuillez sélectionner une nouvelle image ou utiliser une URL HTTP valide')
          } else {
            // Pas d'URL : juste alt et caption
            contentToSave = {
              alt: editData.content?.alt || editData.title || "Image",
              caption: editData.content?.caption || ""
            }
          }
        }
      }
      
      // Préparer les données à envoyer
      const updateData = {
        title: editData.title,
        content: contentToSave,
        duration: editData.duration
      }
      
      // Si c'est un bloc IMAGE avec un nouveau fichier, ajouter le fichier
      // Le champ doit s'appeler "image" selon la spécification de l'API
      if (selectedBlock.type === 'IMAGE' && imageFile) {
        updateData.image = imageFile
      }
      
      // LOG: Données envoyées depuis EditorCanvas
      console.log('[EditorCanvas] Données envoyées pour modification du bloc:', {
        blockId: selectedBlock.id,
        blockType: selectedBlock.type,
        updateData: {
          ...updateData,
          image: updateData.image ? {
            name: updateData.image.name,
            type: updateData.image.type,
            size: updateData.image.size,
            isFile: updateData.image instanceof File
          } : undefined
        },
        contentToSave,
        hasImageFile: !!imageFile,
        imageFileInfo: imageFile ? {
          name: imageFile.name,
          type: imageFile.type,
          size: imageFile.size,
          isFile: imageFile instanceof File
        } : null
      })
      
      const result = await onBlockUpdate(selectedBlock.id, updateData)
      
      // LOG: Réponse reçue dans EditorCanvas
      console.log('[EditorCanvas] Réponse reçue après modification:', result)
      setIsEditing(false)
      setImageFile(null) // Réinitialiser après sauvegarde réussie
    } catch (err) {
      console.error('Error saving block:', err)
      throw err // Re-throw pour que le composant parent puisse gérer l'erreur
    } finally {
      setUploadingImage(false)
    }
  }

  const handleCancel = () => {
    if (selectedBlock) {
      setEditData({
        title: selectedBlock.title || '',
        content: selectedBlock.content || {},
        type: selectedBlock.type,
        duration: selectedBlock.duration || 0
      })
    }
    setIsEditing(false)
    setPreviewMode(false)
  }

  const getBlockIcon = (type) => {
    switch (type) {
      case 'TEXT':
        return <FileText className="w-5 h-5" />
      case 'IMAGE':
        return <ImageIcon className="w-5 h-5" />
      case 'VIDEO':
        return <Video className="w-5 h-5" />
      case 'ASSIGNMENT':
        return <HelpCircle className="w-5 h-5" />
      case 'DOWNLOAD':
        return <Download className="w-5 h-5" />
      default:
        return <FileText className="w-5 h-5" />
    }
  }

  const renderBlockEditor = () => {
    if (!selectedBlock || !editData) return null

    switch (selectedBlock.type) {
      case 'TEXT':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du bloc
              </label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Titre du bloc"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenu
              </label>
              <TipTapEditor
                content={editData.content?.html || ''}
                onChange={(html) => setEditData(prev => ({
                  ...prev,
                  content: { ...prev.content, html }
                }))}
                placeholder="Saisissez le contenu..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée (minutes)
              </label>
              <input
                type="number"
                value={editData.duration || 0}
                onChange={(e) => setEditData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                min="0"
              />
            </div>
          </div>
        )

      case 'IMAGE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du bloc
              </label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Titre du bloc"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image
              </label>
              <ImageUpload
                currentImage={editData.content?.imageUrl || ''}
                onImageSelect={(file) => {
                  if (file) {
                    // Stocker le fichier pour l'uploader plus tard
                    setImageFile(file)
                    // Créer une data URL pour l'aperçu uniquement
                    const reader = new FileReader()
                    reader.onload = (e) => {
                      setEditData(prev => ({
                        ...prev,
                        content: { ...prev.content, imageUrl: e.target.result }
                      }))
                    }
                    reader.readAsDataURL(file)
                  } else {
                    // Supprimer l'image
                    setImageFile(null)
                    setEditData(prev => ({
                      ...prev,
                      content: { ...prev.content, imageUrl: '' }
                    }))
                  }
                }}
              />
              {imageFile && (
                <p className="text-sm text-[#203a8a] mt-2">
                  Nouvelle image sélectionnée : {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              {uploadingImage && (
                <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Upload de l'image en cours...
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Légende
              </label>
              <textarea
                value={editData.content?.caption || ''}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  content: { ...prev.content, caption: e.target.value }
                }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Légende de l'image"
              />
            </div>
          </div>
        )

      case 'VIDEO':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du bloc
              </label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Titre du bloc"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de la vidéo
              </label>
              <input
                type="url"
                value={editData.content?.videoUrl || ''}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  content: { ...prev.content, videoUrl: e.target.value }
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="https://youtube.com/watch?v=... ou https://vimeo.com/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transcription
              </label>
              <textarea
                value={editData.content?.transcript || ''}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  content: { ...prev.content, transcript: e.target.value }
                }))}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Transcription de la vidéo"
              />
            </div>
          </div>
        )

      default:
        return (
          <div className="text-center py-8 text-gray-500">
            Type de bloc non supporté pour l'édition
          </div>
        )
    }
  }

  const renderBlockPreview = () => {
    if (!selectedBlock) return null

    switch (selectedBlock.type) {
      case 'TEXT':
        return <TextBlock block={selectedBlock} />
      case 'IMAGE':
        return <ImageBlock block={selectedBlock} />
      case 'VIDEO':
        return <VideoBlock block={selectedBlock} />
      case 'ASSIGNMENT':
        return <AssignmentBlock block={selectedBlock} />
      case 'DOWNLOAD':
        return <DownloadBlock block={selectedBlock} />
      default:
        return (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-gray-500">Type de bloc non supporté</p>
            </CardContent>
          </Card>
        )
    }
  }

  // No block selected
  if (!selectedBlockId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          {isCreatingNew ? (
            <>
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Commencez par remplir les informations du cours
              </h2>
              <p className="text-gray-600 mb-6">
                Utilisez le bouton "Informations" dans le header pour définir le titre, la description, le prix et les autres paramètres de votre cours.
              </p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">📝</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                {selectedModuleId ? 'Sélectionnez un bloc pour commencer' : 'Commencez par créer un module'}
              </h2>
              <p className="text-gray-600 mb-6">
                {selectedModuleId 
                  ? 'Choisissez un bloc dans la sidebar ou créez-en un nouveau pour commencer l\'édition.'
                  : 'Créez votre premier module dans la sidebar pour commencer à structurer votre cours.'
                }
              </p>
              {selectedModuleId && onCreateBlock && (
                <Button
                  type="button"
                  onClick={() => onCreateBlock(null)}
                  className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un bloc
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  // Loading state
  if (blocksLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
          <p className="text-gray-600">Chargement du bloc...</p>
        </div>
      </div>
    )
  }

  // No block found
  if (!selectedBlock) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500">Bloc non trouvé</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {getBlockIcon(selectedBlock.type)}
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedBlock.title || 'Sans titre'}
            </h3>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className="h-8 hover:bg-gray-100"
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Édition' : 'Aperçu'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
            className="h-8 hover:bg-gray-100"
          >
            <Edit className="w-4 h-4 mr-2" />
            {isEditing ? 'Annuler' : 'Modifier'}
          </Button>
          {isEditing && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="h-8 hover:bg-gray-100"
              >
                Annuler
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                className="h-8 bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                Sauvegarder
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {isEditing ? (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-6">
            {renderBlockEditor()}
          </div>
        ) : previewMode ? (
          <div className="max-w-4xl mx-auto">
            {renderBlockPreview()}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-6">
            <div className="prose max-w-none">
              {renderBlockPreview()}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Supprimer le bloc"
        message="Êtes-vous sûr de vouloir supprimer ce bloc ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={() => {
          if (selectedBlock) {
            onBlockDelete(selectedBlock.id)
          }
        }}
      />
    </div>
  )
}

